'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

interface AnalyticsTrackerProps {
    children?: React.ReactNode;
}

export default function AnalyticsTracker({ children }: AnalyticsTrackerProps) {
    const pathname = usePathname();
    const sessionIdRef = useRef<string | null>(null);
    const isTrackingRef = useRef<boolean>(false);

    // Generate or retrieve session ID
    useEffect(() => {
        let sessionId = sessionStorage.getItem('analytics_session_id');
        if (!sessionId) {
            sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            sessionStorage.setItem('analytics_session_id', sessionId);
        }
        sessionIdRef.current = sessionId;
    }, []);

    // Track page views
    useEffect(() => {
        if (!sessionIdRef.current || isTrackingRef.current) return;

        const trackPageView = async () => {
            isTrackingRef.current = true;
            try {
                // Get referrer from URL params or document referrer
                let referrer = '';
                try {
                    const urlParams = new URLSearchParams(window.location.search);
                    const referrerParam = urlParams.get('referrer');
                    referrer = referrerParam || document.referrer || '';
                } catch (urlError) {
                    console.warn('Error parsing URL parameters:', urlError);
                    referrer = document.referrer || '';
                }

                const response = await fetch('/api/analytics/track', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        sessionId: sessionIdRef.current,
                        page: pathname,
                        title: document.title || '',
                        referrer: referrer,
                        action: 'page_view'
                    }),
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.sessionId) {
                        sessionIdRef.current = data.sessionId;
                        sessionStorage.setItem('analytics_session_id', data.sessionId);
                    }
                }
            } catch (error) {
                console.error('Failed to track page view:', error);
                // Don't let analytics errors break the page
            } finally {
                isTrackingRef.current = false;
            }
        };

        // Small delay to ensure document.title is set
        const timer = setTimeout(trackPageView, 100);
        return () => clearTimeout(timer);
    }, [pathname]);

    // Track page changes
    useEffect(() => {
        if (!sessionIdRef.current) return;

        const updatePage = async () => {
            try {
                // Get referrer from URL params or document referrer
                let referrer = '';
                try {
                    const urlParams = new URLSearchParams(window.location.search);
                    const referrerParam = urlParams.get('referrer');
                    referrer = referrerParam || document.referrer || '';
                } catch (urlError) {
                    console.warn('Error parsing URL parameters:', urlError);
                    referrer = document.referrer || '';
                }

                await fetch('/api/analytics/track', {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        sessionId: sessionIdRef.current,
                        action: 'update_page',
                        data: {
                            page: pathname,
                            title: document.title || '',
                            referrer: referrer
                        }
                    }),
                });
            } catch (error) {
                console.error('Failed to update page tracking:', error);
            }
        };

        // Small delay to ensure document.title is updated
        const timer = setTimeout(updatePage, 100);
        return () => clearTimeout(timer);
    }, [pathname]);

    // Track session end on page unload
    useEffect(() => {
        const handleBeforeUnload = async () => {
            if (!sessionIdRef.current) return;

            // Use sendBeacon for reliable tracking on page unload
            const data = JSON.stringify({
                sessionId: sessionIdRef.current,
                action: 'end_session'
            });

            if (navigator.sendBeacon) {
                navigator.sendBeacon('/api/analytics/track', data);
            } else {
                // Fallback for older browsers
                try {
                    await fetch('/api/analytics/track', {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: data,
                        keepalive: true
                    });
                } catch (error) {
                    console.error('Failed to end session:', error);
                }
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                handleBeforeUnload();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Heartbeat to keep session active
    useEffect(() => {
        if (!sessionIdRef.current) return;

        const heartbeat = setInterval(async () => {
            try {
                await fetch('/api/analytics/track', {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        sessionId: sessionIdRef.current,
                        action: 'heartbeat'
                    }),
                });
            } catch {
                // Silently fail heartbeat
            }
        }, 60000); // Send heartbeat every 60 seconds

        return () => clearInterval(heartbeat);
    }, []);

    return <>{children}</>;
}
